import React, { useEffect, useState } from 'react';
import { Play, Pause, RotateCcw, Rewind, FastForward } from 'lucide-react';
import { useSpin } from '../../context/SpinContext';
import DropZone from './DropZone';
import WaveformTrimmer from './WaveformTrimmer';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';

const TRIM_PRESETS_SECONDS = [30, 60, 90];
const NO_AUDIO_PRESETS_SECONDS = [15, 30, 60];

// Trim start/end are edited via local, uncontrolled-feeling text state and
// only committed to the audio engine on blur / Enter. Committing (and
// re-formatting with .toFixed) on every keystroke was fighting the user's
// typing -- the input value would snap to "1.0" mid-type and swallow
// further digits.
function TrimNumberInput({ label, value, onCommit, testId }) {
  const [text, setText] = useState(value.toFixed(1));

  useEffect(() => {
    setText(value.toFixed(1));
  }, [value]);

  const commit = () => {
    const parsed = Number.parseFloat(text);
    if (Number.isFinite(parsed)) {
      onCommit(parsed);
    } else {
      setText(value.toFixed(1));
    }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-neutral-600 mb-1">{label}</label>
      <Input
        type="number"
        step="0.1"
        min={0}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        className="h-8 text-sm"
        data-testid={testId}
      />
    </div>
  );
}

export default function AudioControls() {
  const { audio } = useSpin();
  const [customMode, setCustomMode] = useState(false);
  const [presetSeconds, setPresetSeconds] = useState(30);

  // Every newly-uploaded file starts back in "quick pick" mode at the 30s
  // default (the audio engine already seeds trimEnd to min(30, duration)) --
  // unless the track itself is shorter than the smallest preset, in which
  // case every preset button would be disabled, so default straight to
  // Custom so the user isn't stuck with no usable control.
  useEffect(() => {
    if (audio.fileName) {
      const shorterThanSmallestPreset = audio.duration > 0 && audio.duration < TRIM_PRESETS_SECONDS[0];
      setCustomMode(shorterThanSmallestPreset);
      setPresetSeconds(30);
    }
  }, [audio.fileName, audio.duration]);

  const applyPreset = (seconds) => {
    setPresetSeconds(seconds);
    const len = Math.min(seconds, audio.duration);
    let start = audio.trimStart;
    if (start + len > audio.duration) start = Math.max(0, audio.duration - len);
    audio.setTrim(start, start + len);
  };

  const handleCustomToggle = (checked) => {
    setCustomMode(checked);
    if (!checked) applyPreset(presetSeconds);
  };

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer" data-testid="no-audio-checkbox-label">
        <Checkbox
          checked={audio.noAudio}
          onCheckedChange={(v) => audio.setNoAudio(!!v)}
          data-testid="no-audio-checkbox"
        />
        Generate without audio (just the spinning image)
      </label>

      {audio.noAudio ? (
        <div className="space-y-3" data-testid="no-audio-duration-panel">
          <p className="text-xs text-neutral-400">No music needed — pick how long the silent video should be.</p>
          <div className="flex gap-1.5" data-testid="no-audio-duration-presets">
            {NO_AUDIO_PRESETS_SECONDS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => audio.setNoAudioDuration(n)}
                className={`min-h-[36px] px-3 rounded-lg text-xs font-medium border transition-colors ${
                  audio.noAudioDuration === n
                    ? 'bg-orange-50 border-orange-300 text-orange-700'
                    : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                }`}
                data-testid={`no-audio-preset-${n}s-button`}
              >
                {n}s
              </button>
            ))}
          </div>
          <div className="w-32">
            <label className="block text-xs font-medium text-neutral-600 mb-1">Custom length (s)</label>
            <Input
              type="number"
              min={1}
              max={300}
              step={1}
              value={audio.noAudioDuration}
              onChange={(e) => {
                const v = Number.parseFloat(e.target.value);
                if (Number.isFinite(v)) audio.setNoAudioDuration(v);
              }}
              className="h-8 text-sm"
              data-testid="no-audio-duration-input"
            />
          </div>
        </div>
      ) : (
        <>
          <DropZone
            accept=".mp3,.wav,.m4a,.flac,.mp4,.mov,.webm,.mkv,audio/*,video/*"
            hint="mp3, wav, m4a, flac — or a video (mp4/mov/webm/mkv) to use its audio"
            onFile={audio.loadFile}
            fileName={audio.fileName}
            onClear={audio.reset}
            error={audio.error}
            isAudio
          />

          {audio.isLoading && (
            <p className="text-xs text-neutral-400" data-testid="audio-loading-status">
              {audio.isExtracting
                ? `Extracting audio from your video\u2026 ${Math.round((audio.extractProgress || 0) * 100)}%`
                : 'Decoding audio\u2026'}
            </p>
          )}

          {audio.peaks && (
            <>
              <WaveformTrimmer
                peaks={audio.peaks}
                duration={audio.duration}
                trimStart={audio.trimStart}
                trimEnd={audio.trimEnd}
                currentTime={audio.currentTime}
                isPlaying={audio.isPlaying}
                onTrimChange={(s, e) => audio.setTrim(s, e)}
                lockLength={!customMode}
              />

              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex gap-1.5" data-testid="trim-duration-presets">
                  {TRIM_PRESETS_SECONDS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => applyPreset(n)}
                      disabled={customMode || n > audio.duration + 0.05}
                      className={`min-h-[36px] px-3 rounded-lg text-xs font-medium border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                        !customMode && presetSeconds === n
                          ? 'bg-orange-50 border-orange-300 text-orange-700'
                          : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                      }`}
                      data-testid={`trim-preset-${n}s-button`}
                    >
                      {n}s
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-1.5 text-xs text-neutral-500 cursor-pointer" data-testid="custom-trim-checkbox-label">
                  <Checkbox
                    checked={customMode}
                    onCheckedChange={(v) => handleCustomToggle(!!v)}
                    data-testid="custom-trim-checkbox"
                  />
                  Custom
                </label>
              </div>

              <div className="flex items-center justify-center gap-3">
                <button type="button" onClick={audio.restart} className="p-2 rounded-full hover:bg-neutral-100 text-neutral-600" data-testid="audio-restart-button">
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => audio.skip(-5)} className="p-2 rounded-full hover:bg-neutral-100 text-neutral-600" data-testid="audio-rewind-button">
                  <Rewind className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => (audio.isPlaying ? audio.pause() : audio.play())}
                  className="w-10 h-10 rounded-full bg-neutral-900 hover:bg-neutral-800 text-white flex items-center justify-center"
                  data-testid="audio-play-pause-button"
                >
                  {audio.isPlaying ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
                </button>
                <button type="button" onClick={() => audio.skip(5)} className="p-2 rounded-full hover:bg-neutral-100 text-neutral-600" data-testid="audio-forward-button">
                  <FastForward className="w-4 h-4" />
                </button>
              </div>

              {customMode && (
                <div className="grid grid-cols-2 gap-3">
                  <TrimNumberInput
                    label="Start (s)"
                    value={audio.trimStart}
                    onCommit={(v) => audio.setTrim(v, audio.trimEnd)}
                    testId="audio-trim-start-input"
                  />
                  <TrimNumberInput
                    label="End (s)"
                    value={audio.trimEnd}
                    onCommit={(v) => audio.setTrim(audio.trimStart, v)}
                    testId="audio-trim-end-input"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-1">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-neutral-600">Fade in</label>
                    <span className="text-xs text-neutral-400 font-mono">{audio.fadeIn.toFixed(1)}s</span>
                  </div>
                  <Slider min={0} max={3} step={0.1} value={[audio.fadeIn]} onValueChange={(v) => audio.setFadeIn(v[0])} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-neutral-600">Fade out</label>
                    <span className="text-xs text-neutral-400 font-mono">{audio.fadeOut.toFixed(1)}s</span>
                  </div>
                  <Slider min={0} max={3} step={0.1} value={[audio.fadeOut]} onValueChange={(v) => audio.setFadeOut(v[0])} />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
